import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { usersApiUrl } from '../../lib/serviceUrls';
import { SwissButton, SwissPanel, SwissSectionHeader } from '../swiss';
import { isValidPhone, normalisePhoneInput } from './AlertSettingsForm';

function formatMobile(number) {
  return /^04\d{8}$/.test(number) ? `${number.slice(0, 4)} ${number.slice(4, 7)} ${number.slice(7)}` : number;
}

export function ContactVerificationPanel({ metadata = {}, manageConsent = false, onSaved, compact = false, draftPhone, onStateChange }) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const phone = normalisePhoneInput(metadata.phone || '');
  const email = String(metadata.email || '').trim().toLowerCase();
  const [state, setState] = useState(null);
  const stateListener = useRef(onStateChange);
  stateListener.current = onStateChange;
  useEffect(() => { stateListener.current?.(state); }, [state]);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sendUncertain, setSendUncertain] = useState(false);
  const [now, setNow] = useState(Date.now());
  const busyRef = useRef(false);
  const challengeRef = useRef(null);
  const acceptChallenge = useCallback(next => {
    const previous = challengeRef.current;
    if (!next || previous?.challengeId !== next.challengeId || previous?.expiresAt !== next.expiresAt || previous?.number !== next.number) setCode('');
    challengeRef.current = next; setChallenge(next);
  }, []);
  const revision = useRef(0);
  const call = useCallback(async (path, body) => {
    const token = await getToken();
    if (!token) throw new Error('Your session has expired. Please sign in again.');
    const response = await fetch(usersApiUrl(path), {
      method: body === undefined ? 'GET' : 'POST',
      headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      if (response.status === 401) throw new Error('Your session has expired. Please sign in again.');
      const failure = new Error(typeof data?.error === 'string' && data.error.length <= 300 ? data.error : 'We could not update alert permissions. Please try again.');
      failure.definitive = response.status >= 400 && response.status < 500;
      throw failure;
    }
    if (!data || typeof data !== 'object') throw new Error('We could not load alert permissions. Please try again.');
    return data;
  }, [getToken]);
  const reload = useCallback(async version => {
    const next = await call('/alerts/contact');
    if (version !== revision.current) return;
    setState(next);
    const recovered = next.smsVerified === true || next.smsLegacyEligible === true ? null : next.smsChallenge;
    if (recovered && typeof recovered.challengeId === 'string' && Number.isSafeInteger(recovered.expiresAt) && typeof recovered.simulated === 'boolean') {
      acceptChallenge({ ...recovered, number: phone });
    } else { acceptChallenge(null); }
    setSendUncertain(false);
  }, [call, phone, acceptChallenge]);
  useEffect(() => {
    // A revision counter, not a DOM node: the cleanup must bump its latest value.
    const revisions = revision;
    const version = ++revision.current;
    setState(null); acceptChallenge(null); setCode(''); setMessage(''); setError(''); setSendUncertain(false); setBusy(true); busyRef.current=true;
    reload(version).catch(failure => { if (version === revision.current) setError(failure.message); })
      .finally(() => { if (version === revision.current) { setBusy(false); busyRef.current=false; } });
    return () => { revisions.current++; };
  }, [user?.id, phone, email, reload, acceptChallenge]);
  useEffect(() => {
    if (!challenge) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [challenge]);
  const perform = async action => {
    if (busyRef.current) return;
    busyRef.current=true;
    const version = revision.current; setBusy(true); setError(''); setMessage('');
    try { await action(version); } catch (failure) { if (version === revision.current) setError(failure.message); }
    finally { if (version === revision.current) { setBusy(false); busyRef.current=false; } }
  };
  const start = () => perform(async version => {
    if (challenge || sendUncertain) { await reload(version); return; }
    let next;
    try { next = await call('/alerts/contact/sms/start', { number: phone }); }
    catch (failure) {
      if (version === revision.current) setSendUncertain(!failure.definitive);
      // A read can recover a lost response; it never requests another SMS.
      await reload(version).catch(() => {});
      throw failure;
    }
    if (typeof next.challengeId !== 'string' || !Number.isSafeInteger(next.expiresAt) || typeof next.simulated !== 'boolean') {
      if (version === revision.current) setSendUncertain(true);
      throw new Error('We could not confirm the verification request. Refresh its status before continuing.');
    }
    if (version === revision.current) { acceptChallenge({ ...next, number: phone }); setNow(Date.now()); }
  });
  const expired = !!challenge && challenge.expiresAt * 1000 <= now;
  const held = !!challenge && (challenge.resendHeld === true || ['unknown', 'reserved', 'sending'].includes(challenge.sendOutcome));
  const verify = () => perform(async version => {
    if (!challenge || expired || held || !/^\d{6}$/.test(code)) return;
    let result;
    try { result = await call('/alerts/contact/sms/verify', { number: challenge.number, challengeId: challenge.challengeId, code }); }
    finally { if (version === revision.current) setCode(''); }
    if (version !== revision.current) return;
    acceptChallenge(null); setCode('');
    setMessage(result.verified === true ? 'Your mobile number is verified.' : 'Development check completed. This number is still unverified for real SMS alerts.');
    await reload(version);
  });
  const consent = (source, channel, enabled) => perform(async version => {
    await call('/alerts/consent', { source, channel, enabled, expectedVersion: state.consent[source][channel].version });
    await reload(version);
  });
  const disableAll = () => perform(async version => {
    await call('/alerts/consent', { source: 'all', channel: 'all', enabled: false });
    await reload(version);
    if (version === revision.current) await onSaved?.();
    if (version === revision.current) setMessage('All wine and marketplace alert permissions are off.');
  });
  const savedMobileEligible = state?.smsLegacyEligible === true;
  const mobileStatus = !state ? null : state.smsVerified === true ? 'Verified'
    : savedMobileEligible ? 'Saved mobile' : state.smsSimulated === true ? 'Simulated only, not verified' : 'Not verified';
  const numberChanged = draftPhone !== undefined && normalisePhoneInput(draftPhone) !== phone;
  if (compact) return <div className="ff-mobile-assurance">
    {error && <div role="alert" className="ff-field__error">{error} <SwissButton type="button" variant="ghost" disabled={busy} onClick={() => perform(reload)}>Retry verification status</SwissButton></div>}
    {message && <p role="status" className="ff-field__hint">{message}</p>}
    {busy && <span role="status" className="ff-field__hint">{state ? 'Updating mobile…' : 'Checking mobile…'}</span>}
    {numberChanged ? <p className="ff-field__hint">Save your new mobile, then verify it for SMS alerts.</p> : phone && mobileStatus && <>
      <span className={`ff-account-badge${state.smsVerified || savedMobileEligible ? '' : ' ff-account-badge--muted'}`}>{mobileStatus}</span>
      {savedMobileEligible && <p className="ff-field__hint">Your existing SMS alerts can continue without a new code.</p>}
      {!savedMobileEligible && !state.smsVerified && <p className="ff-field__hint">Verify this mobile to receive SMS alerts.</p>}
    </>}
    {!numberChanged && state?.suppressions?.sms && <p className="ff-field__error">SMS alerts are stopped by your provider. Verification does not undo STOP.</p>}
    {!numberChanged && phone && state && !state.smsVerified && !savedMobileEligible && <div className="ff-mobile-assurance__actions">
      {!challenge && !sendUncertain && <SwissButton type="button" variant="ghost" disabled={busy || !isValidPhone(phone)} onClick={start}>Request verification code</SwissButton>}
      {(challenge || sendUncertain || error) && <SwissButton type="button" variant="ghost" disabled={busy} onClick={() => perform(reload)}>Refresh verification status</SwissButton>}
    </div>}
    {sendUncertain && !challenge && <p role="status" className="ff-field__hint">Request status is uncertain. Refresh before requesting another code.</p>}
    {!numberChanged && challenge && challenge.number === phone && <div className="ff-contact-code" role="group" aria-label="Verify your mobile number">
      {held && <p role="status" className="ff-field__hint">This SMS request is awaiting provider confirmation. Refresh its status; no new text will be sent.</p>}
      {expired && <p role="status" className="ff-field__hint">This code has expired. Refresh its status before continuing.</p>}
      {challenge.simulated && <p className="ff-field__hint">No SMS was sent. Use development code 000000; this will not verify real SMS.</p>}
      <label className="ff-field__label" htmlFor="sms-verification-code">Verification code</label>
      <input id="sms-verification-code" className="ff-input" inputMode="numeric" autoComplete="one-time-code" value={code} maxLength={6} disabled={busy || expired || held} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); verify(); } }} />
      <SwissButton type="button" disabled={busy || expired || held || !/^\d{6}$/.test(code)} onClick={verify}>Confirm verification code</SwissButton>
    </div>}
  </div>;
  return <SwissPanel>
    <SwissSectionHeader title="Verification & permissions" subtitle="Alerts are sent only on channels you switch on above. New or changed mobiles need verification. Verifying a contact never turns alerts on." />
    {error && <div role="alert" className="ff-notice ff-notice--error">{error}<div className="ff-notice__actions"><SwissButton type="button" variant="ghost" className="ff-button--sm" disabled={busy} onClick={() => perform(reload)}>Retry</SwissButton></div></div>}
    {message && <p role="status" className="ff-notice ff-notice--success">{message}</p>}
    {busy && <div role="status" className="ff-loading ff-contact-loading"><span className="ff-loading__spinner" aria-hidden="true" /><span>{state ? 'Updating alert permissions…' : 'Loading verification status…'}</span></div>}
    <h3 className="ff-account-subheading">Mobile for SMS alerts</h3>
    <div className="ff-account-detail-row">
      <span>{phone ? formatMobile(phone) : 'No mobile saved'}</span>
      {phone && mobileStatus && <span className={`ff-account-badge${state.smsVerified === true || savedMobileEligible ? '' : ' ff-account-badge--muted'}`}>{mobileStatus}</span>}
    </div>
    <p className="ff-field__hint ff-contact-hint">{savedMobileEligible && phone ? 'Your existing SMS alerts can continue at this saved mobile without a new verification code. If you change your number, verify the new mobile before receiving SMS alerts. Email alerts need a verified account email.' : phone ? 'New or changed mobiles need verification before receiving SMS alerts. Email alerts need a verified account email.' : 'Save an Australian mobile in Contact details above to verify it.'}</p>
    {sendUncertain && !challenge && <p className="ff-notice">The verification request could not be confirmed. Do not request another text; refresh its status first.</p>}
    <div className="ff-contact-actions">
    {!savedMobileEligible && <SwissButton type="button" disabled={busy || !state || !isValidPhone(phone) || state.smsVerified === true || !!challenge || sendUncertain} onClick={start}>Request verification code</SwissButton>}
    <SwissButton type="button" variant="ghost" disabled={busy} onClick={() => perform(reload)}>Refresh verification status</SwissButton>
    </div>
    {challenge && challenge.number === phone && <form className="ff-contact-code" aria-label="Verify your mobile number" onSubmit={event => { event.preventDefault(); verify(); }}>
      {held && <p role="status" className="ff-notice">The SMS send has an uncertain outcome and is held for review. No new text will be requested. If a code arrived, keep it private; verification stays off until the provider report confirms this send.</p>}
      {expired && <p role="status" className="ff-notice">This verification code has expired. Refresh the status before continuing.</p>}
      {!held && !expired && challenge.simulated === false && <p className="ff-notice">Enter the code for this existing request. Refreshing does not send another text or extend its expiry.</p>}
      {challenge.simulated === true && <p className="ff-notice">Development simulation: no SMS was sent. Use code 000000. This does not verify your number for real alerts.</p>}
      <label className="ff-field__label" htmlFor="sms-verification-code">Verification code</label>
      <input id="sms-verification-code" className="ff-input" inputMode="numeric" autoComplete="one-time-code" value={code} maxLength={6} disabled={busy || expired || held} aria-describedby="sms-code-help" onChange={event => setCode(event.target.value.replace(/\D/g, ''))} />
      <p id="sms-code-help" className="ff-field__hint">{challenge.simulated ? 'Enter the development code shown above.' : 'Enter the six-digit code from your verification text.'}</p>
      <SwissButton type="submit" disabled={busy || expired || held || !/^\d{6}$/.test(code)}>Confirm verification code</SwissButton>
    </form>}
    {state?.consent && <>
      <h3 className="ff-account-subheading">Alert permissions</h3>
      <p className="ff-field__hint ff-contact-hint">Recorded when you save your alert channels above. Provider opt-outs and complaints always take precedence.</p>
      <ul className="ff-account-connected-list ff-contact-permissions" aria-label="Alert permissions">
        {['wine', 'marketplace'].map(source => ['email', 'sms'].map(channel => {
          const current = state.consent[source]?.[channel]; if (!current) return null;
          const name = `${source === 'wine' ? 'Wine' : 'Marketplace'} ${channel === 'sms' ? 'SMS' : 'email'}`;
          const blocked = state.suppressions?.[channel] === true;
          return <li className="ff-account-detail-row" key={`${source}-${channel}`}>
            <span>{name}</span>
            <span className="ff-contact-permission">
              <span className={`ff-account-badge${current.enabled === true && !blocked ? '' : ' ff-account-badge--muted'}`}>{current.enabled === true ? 'On' : 'Off'}{blocked ? ' · blocked by provider' : ''}</span>
              {manageConsent && <button type="button" role="switch" aria-label={`${name} permission`} aria-checked={current.enabled === true} className="ff-switch" disabled={busy || blocked} onClick={() => consent(source, channel, !current.enabled)} />}
            </span>
          </li>;
        }))}
      </ul>
    </>}
    <div className="ff-contact-stop">
      <SwissButton type="button" variant="ghost" disabled={busy} onClick={disableAll}>Turn off all deal alerts</SwissButton>
      <p className="ff-field__hint">Stops every wine and marketplace alert on every channel. To receive alerts again, switch channels on above and save.</p>
    </div>
  </SwissPanel>;
}
export default ContactVerificationPanel;
